/**
 * When the browser may report one of its own failures (8 Oct).
 *
 * Import-free so `tests/client-error-throttle.test.mjs` can load it directly.
 * Two limits: the same failure (kind + message + screen) once per
 * `SAME_FAILURE_MS`, and at most `MAX_PER_WINDOW` reports per `WINDOW_MS`
 * from one tab — a screen stuck in a crash loop is one problem, not a flood.
 * The server limits again per person.
 */

export const SAME_FAILURE_MS = 10 * 60 * 1000;
export const WINDOW_MS = 10 * 60 * 1000;
export const MAX_PER_WINDOW = 5;

export interface ReportThrottle {
  /** True (and remembered) when a report with this key may go now. */
  allow: (key: string, now: number) => boolean;
}

export function createReportThrottle(): ReportThrottle {
  const lastByKey = new Map<string, number>();
  let sent: number[] = [];
  return {
    allow(key: string, now: number): boolean {
      sent = sent.filter((at) => now - at < WINDOW_MS);
      const last = lastByKey.get(key);
      if (last !== undefined && now - last < SAME_FAILURE_MS) {
        return false;
      }
      if (sent.length >= MAX_PER_WINDOW) {
        return false;
      }
      sent.push(now);
      lastByKey.set(key, now);
      return true;
    },
  };
}

/**
 * Browser warnings that are not failures, never reported (21 Oct).
 *
 * "ResizeObserver loop completed with undelivered notifications" (and the
 * older "ResizeObserver loop limit exceeded") are the browser saying a layout
 * took one more frame to settle. Nothing broke, so nothing is logged and no
 * Teams alert goes out; every other script error is still reported.
 */
const BROWSER_NOISE: readonly RegExp[] = [/^\s*(uncaught\s+)?(error:\s*)?resizeobserver loop\b/i];

export function isBrowserNoise(message: string | null | undefined): boolean {
  const text = message ?? "";
  return BROWSER_NOISE.some((pattern) => pattern.test(text));
}

/** What a window "error" event carries that helps find the fault. */
export interface ScriptErrorFacts {
  filename?: string | null;
  lineno?: number | null;
  colno?: number | null;
  error?: unknown;
}

/** A browser extension's own script, not ours (28 Oct). */
const EXTENSION_SOURCE = /^(chrome|moz|safari(-web)?|ms-browser)-extension:\/\//i;
const EXTENSION_IN_STACK = /(chrome|moz|safari(-web)?|ms-browser)-extension:\/\//i;

export function isExtensionError(facts: ScriptErrorFacts): boolean {
  const stack = facts.error instanceof Error ? (facts.error.stack ?? "") : "";
  return EXTENSION_SOURCE.test(facts.filename ?? "") || EXTENSION_IN_STACK.test(stack);
}

/**
 * Where a script error happened, for the log (28 Oct): LIVE logged eleven
 * bare "Uncaught" on /ask with nothing to go on. File path only (no host),
 * line:col, the thrown value's type, and the first stack frame of ours.
 */
export function scriptErrorDetail(facts: ScriptErrorFacts): string {
  const parts: string[] = [];
  const file = (facts.filename ?? "").replace(/^[a-z]+:\/\/[^/]+/i, "");
  if (file) {
    parts.push(`at ${file}:${facts.lineno ?? 0}:${facts.colno ?? 0}`);
  }
  const thrown = facts.error;
  if (thrown instanceof Error) {
    parts.push(`${thrown.name}: ${thrown.message}`.slice(0, 160));
    const frame = (thrown.stack ?? "")
      .split("\n")
      .map((line) => line.trim())
      .find((line) => line.startsWith("at ") || line.includes("@"));
    if (frame) {
      parts.push(frame.replace(/[a-z]+:\/\/[^/\s)]+/gi, "").slice(0, 160));
    }
  } else if (thrown !== undefined && thrown !== null) {
    parts.push(`thrown ${typeof thrown}`);
  } else {
    parts.push("no error object");
  }
  return parts.join(" | ");
}
